import { catalogCategories } from "@/mocks/catalogData";

interface CatalogFiltersProps {
  activeCategory: string;
  onCategoryChange: (val: string) => void;
  priceRange: [number, number];
  onPriceChange: (val: [number, number]) => void;
  selectedMaterials: string[];
  onMaterialToggle: (mat: string) => void;
  totalCount: number;
  filteredCount: number;
}

const MATERIALS = [
  "Walnut",
  "White Oak",
  "Smoked Oak",
  "Leather",
  "Boucle",
  "Velvet",
  "Steel",
  "Travertine",
  "Brass",
  "Linen",
];

export default function CatalogFilters({
  activeCategory,
  onCategoryChange,
  priceRange,
  onPriceChange,
  selectedMaterials,
  onMaterialToggle,
  totalCount,
  filteredCount,
}: CatalogFiltersProps) {
  return (
    <aside className="w-full lg:w-64 flex-shrink-0">
      {/* Result count */}
      <p
        className="text-[11px] uppercase tracking-[0.18em] text-[#6C7278] mb-8"
        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
      >
        {filteredCount} of {totalCount} pieces
      </p>

      {/* Category */}
      <div className="mb-8">
        <h3
          className="text-[11px] uppercase tracking-[0.18em] text-[#1A1C1E] mb-4"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          Category
        </h3>
        <ul className="flex flex-col gap-1">
          {catalogCategories.map((cat) => (
            <li key={cat.value}>
              <button
                onClick={() => onCategoryChange(cat.value)}
                className={`w-full text-left px-0 py-1.5 text-sm transition-colors duration-200 cursor-pointer ${
                  activeCategory === cat.value
                    ? "text-[#B8422E] font-semibold"
                    : "text-[#6C7278] hover:text-[#1A1C1E]"
                }`}
                style={{ fontFamily: "'Public Sans', sans-serif" }}
              >
                {activeCategory === cat.value && (
                  <span className="inline-block w-2 h-px bg-[#B8422E] mr-2 align-middle" />
                )}
                {cat.label}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Divider */}
      <div className="w-full h-px bg-[#1A1C1E]/10 mb-8" />

      {/* Price range */}
      <div className="mb-8">
        <h3
          className="text-[11px] uppercase tracking-[0.18em] text-[#1A1C1E] mb-4"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          Price Range
        </h3>
        <div className="flex items-center justify-between mb-3">
          <span
            className="text-sm text-[#1A1C1E] font-medium"
            style={{ fontFamily: "'Public Sans', sans-serif" }}
          >
            ${priceRange[0].toLocaleString()}
          </span>
          <span
            className="text-sm text-[#1A1C1E] font-medium"
            style={{ fontFamily: "'Public Sans', sans-serif" }}
          >
            ${priceRange[1].toLocaleString()}
          </span>
        </div>
        <input
          type="range"
          min={0}
          max={6000}
          step={100}
          value={priceRange[1]}
          onChange={(e) => onPriceChange([priceRange[0], Number(e.target.value)])}
          className="w-full accent-[#B8422E] cursor-pointer"
        />
      </div>

      {/* Divider */}
      <div className="w-full h-px bg-[#1A1C1E]/10 mb-8" />

      {/* Materials */}
      <div className="mb-8">
        <h3
          className="text-[11px] uppercase tracking-[0.18em] text-[#1A1C1E] mb-4"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          Material
        </h3>
        <ul className="flex flex-col gap-2">
          {MATERIALS.map((mat) => (
            <li key={mat}>
              <label className="flex items-center gap-3 cursor-pointer group">
                <span
                  className={`w-4 h-4 flex-shrink-0 border transition-colors duration-200 flex items-center justify-center ${
                    selectedMaterials.includes(mat)
                      ? "bg-[#B8422E] border-[#B8422E]"
                      : "border-[#1A1C1E]/20 group-hover:border-[#B8422E]/50"
                  }`}
                  style={{ borderRadius: "2px" }}
                >
                  {selectedMaterials.includes(mat) && (
                    <i className="ri-check-line text-white text-[10px]" />
                  )}
                </span>
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={selectedMaterials.includes(mat)}
                  onChange={() => onMaterialToggle(mat)}
                />
                <span
                  className="text-sm text-[#6C7278] group-hover:text-[#1A1C1E] transition-colors"
                  style={{ fontFamily: "'Public Sans', sans-serif" }}
                >
                  {mat}
                </span>
              </label>
            </li>
          ))}
        </ul>
      </div>

      {/* Reset */}
      <button
        onClick={() => {
          onCategoryChange("all");
          onPriceChange([0, 6000]);
          selectedMaterials.forEach((m) => onMaterialToggle(m));
        }}
        className="text-[11px] uppercase tracking-[0.14em] text-[#6C7278] hover:text-[#B8422E] transition-colors duration-200 cursor-pointer"
        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
      >
        <i className="ri-refresh-line mr-1.5" />
        Reset Filters
      </button>
    </aside>
  );
}
